import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import axios from "../api/axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ====================== Intersection Observer Hook ======================
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVis(true);
      },
      { threshold }
    );
    if (node) observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, vis];
}

// ====================== Static data (Features, Steps, Stats) ======================
const FEATURES = [
  { icon: "🏪", title: "Point of Sale", desc: "Optimized touch interface. Integrated scanner, multiple payment methods." },
  { icon: "📦", title: "Inventory Management", desc: "Real-time tracking, transfers between warehouses, low-stock alerts, barcodes, variants and serial numbers." },
  { icon: "📊", title: "Advanced Accounting", desc: "Hierarchical chart of accounts, double-entry bookkeeping, balance sheet, income statement, VAT and tax compliance." },
  { icon: "👥", title: "Human Resources", desc: "Employee management, time tracking, leave requests." },
  { icon: "🛒", title: "Sales Management", desc: "Quotes → Orders → Sales. Partial payments, returns, recurring invoicing and notifications." },
  { icon: "📈", title: "Dashboard", desc: "Real-time view, dynamic charts, top products/customers, daily financial activity." },
];

const STEPS = [
  { n: "01", title: "5-minute setup", desc: "Automatic configuration wizard. Database, SMTP, languages and permissions configured in a single flow." },
  { n: "02", title: "Configure your business", desc: "Add your products, warehouses, teams and chart of accounts. Import your data via Excel/CSV." },
  { n: "03", title: "Launch operations", desc: "Touch POS, real-time stock tracking, automatic invoicing. Your team operational within hours." },
  { n: "04", title: "Drive with data", desc: "Dashboards, P&L reports, forecasts. Make decisions based on real numbers." },
];

const STATS = [
  { v: "9+", l: "Integrated modules" },
  { v: "24", l: "Available languages" },
  { v: "100%", l: "VAT compliance" },
  { v: "< 5min", l: "Setup time" },
];

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
];

// ====================== Helpers ======================
function Wave({ from, to, flip = false, height = 96 }) {
  return (
    <div
      aria-hidden="true"
      className="stockpos-wave"
      style={{
        position: "relative",
        height,
        background: from,
        lineHeight: 0,
        transform: flip ? "scaleY(-1)" : "none",
      }}
    >
      <svg
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
      >
        <path d="M0,64 C240,128 480,0 720,48 C960,96 1200,128 1440,64 L1440,120 L0,120 Z" fill={to} />
      </svg>
    </div>
  );
}

function Blob({ color, size = 380, top, left, right, bottom, opacity = 0.18, blur = 100 }) {
  return (
    <div
      aria-hidden="true"
      className="stockpos-blob"
      style={{
        position: "absolute",
        pointerEvents: "none",
        borderRadius: 999,
        width: size,
        height: size,
        background: color,
        filter: `blur(${blur}px)`,
        opacity,
        top,
        left,
        right,
        bottom,
      }}
    />
  );
}

function scrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const offset = 60;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
}

// ====================== Navbar (auth-aware, responsive) ======================
function Navbar({ scrolled, isLoggedIn, navigate }) {
  const { auth, setAuth } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu when the window becomes wide again
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 860) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout API call failed", err);
    }

    localStorage.removeItem("accessToken");
    setAuth({});
    toast.success("Logged out successfully.");
    navigate("/auth/login");
    setMenuOpen(false);
  };

  const goToDashboard = () => {
    const roles = auth?.roles || [];
    if (roles.includes("SYSTEM_ADMIN")) {
      navigate("/admin/dashboard");
    } else if (roles.includes("TENANT_ADMIN")) {
      navigate("/tenant/dashboard");
    } else if (roles.includes("BRANCH_ADMIN")) {
      navigate("/branch/dashboard");
    } else if (roles.includes("STOCK_MANAGER")) {
      navigate("/stock/dashboard");
    } else if (roles.includes("ACCOUNTANT")) {
      navigate("/accountant/dashboard");
    }
    setMenuOpen(false);
  };

  const navLinkStyle = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: "rgba(255,255,255,0.65)",
    textDecoration: "none",
    transition: "color .2s",
    cursor: "pointer",
  };

  return (
    <nav
      className="stockpos-nav"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled || menuOpen ? "rgba(10,22,50,0.97)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(20px)" : "none",
        borderBottom: scrolled || menuOpen ? "1px solid rgba(255,255,255,0.08)" : "none",
        transition: "all 0.35s ease",
      }}
    >
      <div
        className="stockpos-nav-inner"
        style={{ maxWidth: 1200, margin: "0 auto", padding: "0 2rem", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div
          onClick={() => { scrollTo("hero"); setMenuOpen(false); }}
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
        >
          <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 20, color: "#fff", letterSpacing: "-0.5px" }}>
            STOCK<span style={{ color: "#7F77DD" }}>POS</span>
          </span>
        </div>

        {/* Desktop links */}
        <div className="stockpos-nav-links" style={{ display: "flex", gap: 32, alignItems: "center" }}>
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={(e) => { e.preventDefault(); scrollTo(href.replace("#", "")); }}
              style={navLinkStyle}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.65)"; }}
            >
              {label}
            </a>
          ))}

          {!isLoggedIn ? (
            <button
              onClick={() => navigate("/auth/login")}
              style={{
                padding: "8px 20px",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 8,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                backdropFilter: "blur(8px)",
              }}
            >
              Log in
            </button>
          ) : (
            <>
              <button
                onClick={goToDashboard}
                style={{
                  padding: "8px 20px",
                  background: "#7F77DD",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Dashboard
              </button>

              <button
                onClick={handleLogout}
                style={{
                  padding: "8px 16px",
                  background: "transparent",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  marginLeft: 12,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.7)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                }}
              >
                <i className="ni ni-user-run mr-1" style={{ fontSize: 14 }} />
                Logout
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="stockpos-nav-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 8,
            width: 40,
            height: 40,
            cursor: "pointer",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <span style={{ width: 18, height: 2, background: "#fff", display: "block", transition: "transform .2s", transform: menuOpen ? "translateY(6px) rotate(45deg)" : "none" }} />
          <span style={{ width: 18, height: 2, background: "#fff", display: "block", opacity: menuOpen ? 0 : 1, transition: "opacity .2s" }} />
          <span style={{ width: 18, height: 2, background: "#fff", display: "block", transition: "transform .2s", transform: menuOpen ? "translateY(-6px) rotate(-45deg)" : "none" }} />
        </button>
      </div>

      {/* Mobile dropdown (display is handled by the CSS class) */}
      {menuOpen && (
        <div
          className="stockpos-nav-mobile"
          style={{ padding: "8px 2rem 24px", flexDirection: "column", gap: 18 }}
        >
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={(e) => { e.preventDefault(); scrollTo(href.replace("#", "")); setMenuOpen(false); }}
              style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.8)", textDecoration: "none" }}
            >
              {label}
            </a>
          ))}
          {!isLoggedIn ? (
            <button
              onClick={() => { navigate("/auth/login"); setMenuOpen(false); }}
              style={{ padding: "10px 20px", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: 14, fontWeight: 600, border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            >
              Log in
            </button>
          ) : (
            <>
              <button
                onClick={goToDashboard}
                style={{ padding: "10px 20px", background: "#7F77DD", color: "#fff", fontSize: 14, fontWeight: 600, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                style={{ padding: "10px 16px", background: "transparent", color: "rgba(255,255,255,0.7)", fontSize: 14, fontWeight: 500, border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                Logout
              </button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}

// ====================== Hero ======================
function Hero() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const fadeIn = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "none" : "translateY(20px)",
    transition: `all .7s ease ${delay}s`,
  });

  return (
    <section
      id="hero"
      className="stockpos-hero"
      style={{ minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #1E1B4B 0%, #4F46E5 50%, #7F77DD 100%)" }} />
      <Blob color="rgba(127,119,221,0.55)" size={520} top={-160} right={-120} opacity={0.45} />
      <Blob color="rgba(79,70,229,0.55)" size={420} bottom={120} left={-100} opacity={0.4} />
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 240"
        preserveAspectRatio="none"
        style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: 240, display: "block", pointerEvents: "none" }}
      >
        <path d="M0,140 C220,200 440,40 720,90 C1000,140 1220,240 1440,150 L1440,240 L0,240 Z" fill="#0A0A1A" />
        <path d="M0,160 C220,220 440,60 720,110 C1000,160 1220,260 1440,170 L1440,240 L0,240 Z" fill="#0A0A1A" opacity="0.6" />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
          maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, transparent 70%)",
        }}
      />
      <div
        className="stockpos-hero-inner"
        style={{ position: "relative", zIndex: 2, textAlign: "center", padding: "140px 2rem 100px", maxWidth: 860, margin: "0 auto" }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 99,
            padding: "6px 18px",
            marginBottom: 32,
            opacity: mounted ? 1 : 0,
            transform: mounted ? "none" : "translateY(10px)",
            transition: "all .6s ease",
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#7F77DD", display: "inline-block", boxShadow: "0 0 8px #7F77DD" }} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#fff", fontWeight: 500 }}>
            All-in-one platform — Stock · POS · Accounting · HR
          </span>
        </div>
        <h1
          style={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(36px, 8vw, 78px)",
            color: "#fff",
            lineHeight: 1.05,
            letterSpacing: "-2px",
            marginBottom: 24,
            ...fadeIn(0.1),
          }}
        >
          Run your business.
          <br />
          <span style={{ color: "#7F77DD", textShadow: "0 0 40px rgba(127,119,221,0.4)" }}>Automate everything.</span>
        </h1>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "clamp(14px, 3vw, 18px)",
            color: "rgba(255,255,255,0.75)",
            maxWidth: 560,
            margin: "0 auto 44px",
            lineHeight: 1.75,
            ...fadeIn(0.2),
          }}
        >
          STOCKPOS brings inventory, sales, purchasing, accounting and HR together in one platform. Zero paperwork. Decisions backed by real data.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", ...fadeIn(0.3) }}>
          <button
            style={{
              padding: "15px 28px",
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              fontSize: 15,
              fontWeight: 500,
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 10,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              backdropFilter: "blur(8px)",
            }}
          >
            Watch the demo
          </button>
        </div>
      </div>
    </section>
  );
}

// ====================== Stats ======================
function Stats() {
  const [ref, vis] = useInView();
  return (
    <section ref={ref} style={{ background: "#0A0A1A", padding: "20px 1rem 80px", position: "relative", overflow: "hidden" }}>
      <Blob color="rgba(79,70,229,0.32)" size={420} top={-80} right="10%" opacity={0.25} blur={120} />
      <div
        className="stockpos-stats-grid"
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          display: "grid",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 20,
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          background: "rgba(255,255,255,0.02)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {STATS.map((s, i) => (
          <div
            key={s.l}
            className="stockpos-stat-cell"
            style={{
              padding: "36px 20px",
              textAlign: "center",
              opacity: vis ? 1 : 0,
              transform: vis ? "none" : "translateY(16px)",
              transition: `all .5s ease ${i * 0.1}s`,
            }}
          >
            <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "clamp(28px,5vw,40px)", color: "#7F77DD", letterSpacing: "-1px" }}>{s.v}</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 6 }}>{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ====================== Features ======================
function Features() {
  const [ref, vis] = useInView();
  return (
    <section id="features" ref={ref} style={{ background: "#0A0A1A", padding: "60px 1rem 100px", position: "relative", overflow: "hidden" }}>
      <Blob color="rgba(127,119,221,0.32)" size={460} top="20%" left={-120} opacity={0.22} blur={130} />
      <Blob color="rgba(79,70,229,0.32)" size={380} bottom="10%" right={-80} opacity={0.2} blur={120} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#4F46E5", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 14 }}>
            Features
          </div>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "clamp(26px,6vw,50px)", color: "#fff", letterSpacing: "-1.5px", lineHeight: 1.1 }}>
            Everything your
            <br />
            business needs.
          </h2>
        </div>
        <div
          className="stockpos-features-grid"
          style={{ display: "grid", gap: 1, borderRadius: 16, overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)" }}
        >
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="stockpos-feature-cell"
              style={{
                padding: "36px 30px",
                background: "rgba(255,255,255,0.02)",
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateY(24px)",
                transition: `all .55s ease ${i * 0.08}s`,
                cursor: "default",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(79,70,229,0.04)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
            >
              <div style={{ fontSize: 30, marginBottom: 14 }}>{f.icon}</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 18, color: "#fff", marginBottom: 10 }}>{f.title}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ====================== How It Works ======================
function HowItWorks() {
  const [ref, vis] = useInView();
  return (
    <section
      id="how-it-works"
      ref={ref}
      style={{ background: "linear-gradient(180deg, #11132B 0%, #1E1B4B 100%)", padding: "80px 1rem 100px", position: "relative", overflow: "hidden" }}
    >
      <Blob color="rgba(127,119,221,0.3)" size={500} top="20%" right={-140} opacity={0.22} blur={140} />
      <Blob color="rgba(79,70,229,0.32)" size={420} bottom="5%" left={-100} opacity={0.2} blur={130} />
      <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#4F46E5", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 14 }}>
            How it works
          </div>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "clamp(26px,6vw,50px)", color: "#fff", letterSpacing: "-1.5px" }}>
            Up and running within hours.
          </h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className="stockpos-step-row"
              style={{
                display: "flex",
                gap: 28,
                alignItems: "flex-start",
                padding: "28px 24px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                opacity: vis ? 1 : 0,
                transform: vis ? "none" : "translateX(-24px)",
                transition: `all .5s ease ${i * 0.12}s`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(79,70,229,0.04)";
                e.currentTarget.style.borderColor = "rgba(79,70,229,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
              }}
            >
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 40, color: "rgba(79,70,229,0.15)", lineHeight: 1, minWidth: 52, flexShrink: 0 }}>{s.n}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 19, color: "#fff", marginBottom: 8 }}>{s.title}</div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "rgba(255,255,255,0.5)", lineHeight: 1.7 }}>{s.desc}</div>
              </div>
              <div
                className="stockpos-step-arrow"
                style={{
                  marginLeft: "auto",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(79,70,229,0.1)",
                  border: "1px solid rgba(79,70,229,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#4F46E5",
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                →
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ====================== Pricing (dynamic, uses real plans) ======================
function Pricing({ isLoggedIn, navigate, plans }) {
  const [ref, vis] = useInView();
  const { auth } = useAuth();
  const isTenantAdmin = auth?.roles?.includes("TENANT_ADMIN") ?? false;

  // Map plan limits into a human-readable feature list
  const getFeatures = (plan) => {
    const base = [
      `${plan.maxBranches} Branch(es)`,
      `${plan.maxUsersPerBranch} Users / Branch`,
      `${plan.maxProductsPerBranch} Products / Branch`,
    ];
    if (plan.type === "PRO") {
      base.push("Advanced reports");
    } else if (plan.type === "ENTERPRISE") {
      base.push("Advanced reports", "Dedicated 24/7 support");
    }
    return base;
  };

  const planButtonStyle = (isAccent) => ({
    width: "100%",
    padding: "12px",
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    fontWeight: 700,
    background: isAccent ? "#7F77DD" : "transparent",
    color: "#fff",
    border: isAccent ? "none" : "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8,
    cursor: "pointer",
    boxShadow: isAccent ? "0 8px 24px rgba(127,119,221,0.35)" : "none",
  });

  return (
    <section
      id="pricing"
      ref={ref}
      style={{ background: "linear-gradient(180deg,#1E1B4B,#0A0A1A)", padding: "80px 1rem 100px", position: "relative", overflow: "hidden" }}
    >
      <Blob color="rgba(127,119,221,0.32)" size={460} top={-100} left="20%" opacity={0.22} blur={140} />
      <Blob color="rgba(79,70,229,0.32)" size={380} bottom={120} right={-80} opacity={0.2} blur={130} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700, color: "#4F46E5", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 14 }}>
            Pricing
          </div>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: "clamp(26px,6vw,50px)", color: "#fff", letterSpacing: "-1.5px" }}>
            Transparent. No surprises.
          </h2>
        </div>

        {plans.length === 0 ? (
          <div style={{ textAlign: "center", fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: "rgba(255,255,255,0.5)" }}>
            Plans are not available right now. Please try again later.
          </div>
        ) : (
          <div className="stockpos-pricing-grid" style={{ display: "grid", gap: 16, alignItems: "stretch" }}>
            {plans.map((plan, i) => {
              const isAccent = plan.type === "PRO"; // highlight the Pro plan
              const features = getFeatures(plan);
              const priceDisplay = plan.monthlyPrice != null ? String(plan.monthlyPrice) : "Custom";

              return (
                <div
                  key={plan.id || i}
                  style={{
                    padding: "36px 28px",
                    borderRadius: 16,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    background: isAccent ? "linear-gradient(135deg,rgba(79,70,229,0.08),rgba(127,119,221,0.08))" : "rgba(255,255,255,0.02)",
                    border: isAccent ? "1.5px solid rgba(79,70,229,0.35)" : "1px solid rgba(255,255,255,0.07)",
                    opacity: vis ? 1 : 0,
                    transform: vis ? "none" : "translateY(24px)",
                    transition: `all .5s ease ${i * 0.1}s`,
                  }}
                >
                  {isAccent && (
                    <div
                      style={{
                        position: "absolute",
                        top: -13,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "linear-gradient(90deg,#4F46E5,#7F77DD)",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "4px 16px",
                        borderRadius: 99,
                        fontFamily: "'DM Sans',sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Recommended
                    </div>
                  )}

                  <div
                    style={{
                      fontFamily: "'DM Sans',sans-serif",
                      fontSize: 12,
                      fontWeight: 700,
                      color: isAccent ? "#4F46E5" : "rgba(255,255,255,0.4)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 8,
                    }}
                  >
                    {plan.name}
                  </div>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "clamp(30px,6vw,42px)", color: "#fff", letterSpacing: "-1px" }}>
                      {priceDisplay}
                    </span>
                    {priceDisplay !== "Custom" && (
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "rgba(255,255,255,0.35)" }}>{plan.currency}/month</span>
                    )}
                  </div>

                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 28, lineHeight: 1.5 }}>
                    {plan.description}
                  </div>

                  {/* flex: 1 pushes the button to the bottom so all buttons sit on the same line */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32, flex: 1 }}>
                    {features.map((f) => (
                      <div key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: "rgba(255,255,255,0.65)" }}>
                        <span style={{ color: "#4F46E5", flexShrink: 0, marginTop: 1 }}>✓</span>
                        {f}
                      </div>
                    ))}
                  </div>

                  {!isLoggedIn ? (
                    <button
                      onClick={() => navigate("/auth/login")}
                      style={planButtonStyle(isAccent)}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = ".85"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                    >
                      Log in
                    </button>
                  ) : isTenantAdmin ? (
                    <button
                      onClick={() => navigate(`/detail-plan/${plan.id}`)}
                      style={planButtonStyle(isAccent)}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = ".85"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                    >
                      {priceDisplay === "Custom" ? "Contact us" : "Subscribe"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ====================== Footer ======================
function Footer() {
  const linkStyle = {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 12,
    color: "rgba(255,255,255,0.3)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  };

  return (
    <footer style={{ background: "#060F20", borderTop: "1px solid rgba(255,255,255,0.06)", padding: "44px 1.5rem 28px" }}>
      <div
        className="stockpos-footer-inner"
        style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" }}>
            STOCK<span style={{ color: "#4F46E5" }}>POS</span>
          </span>
        </div>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: "rgba(255,255,255,0.2)", textAlign: "center" }}>
          © 2026 STOCKPOS — SOTETEL IT Department
        </div>
        <div style={{ display: "flex", gap: 22 }}>
          {["Privacy", "Terms", "Contact"].map((l) => (
            <button key={l} type="button" style={linkStyle}>
              {l}
            </button>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ====================== App Component (the whole landing page) ======================
export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const navigate = useNavigate();
  const { auth } = useAuth();
  const isLoggedIn = !!(auth?.accessToken || localStorage.getItem("accessToken"));

  // Fetch active plans from the public endpoint
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get("/api/plans/active");
        setActivePlans(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load plans", err);
        setActivePlans([]);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #0A0A1A; color: #fff; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: #060F20; }
        ::-webkit-scrollbar-thumb { background: rgba(79,70,229,0.25); border-radius: 3px; }
        button { transition: all .2s ease; }

        /* ---- Layout grids: default (desktop) ---- */
        .stockpos-stats-grid { grid-template-columns: repeat(4, 1fr); }
        .stockpos-features-grid { grid-template-columns: repeat(3, 1fr); }
        .stockpos-pricing-grid { grid-template-columns: repeat(3, 1fr); }
        .stockpos-stat-cell { border-right: 1px solid rgba(255,255,255,0.07); }
        .stockpos-stat-cell:last-child { border-right: none; }
        .stockpos-feature-cell { border-right: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07); }
        .stockpos-feature-cell:nth-child(3n) { border-right: none; }
        .stockpos-feature-cell:nth-last-child(-n+3) { border-bottom: none; }
        .stockpos-nav-toggle { display: none; }
        .stockpos-nav-mobile { display: none; }

        /* ---- Tablet & small laptop ---- */
        @media (max-width: 1024px) {
          .stockpos-nav-inner { padding: 0 1.25rem; }
          .stockpos-features-grid { grid-template-columns: repeat(2, 1fr); }
          .stockpos-feature-cell:nth-child(3n) { border-right: 1px solid rgba(255,255,255,0.07); }
          .stockpos-feature-cell:nth-child(2n) { border-right: none; }
          .stockpos-feature-cell:nth-last-child(-n+3) { border-bottom: 1px solid rgba(255,255,255,0.07); }
          .stockpos-feature-cell:nth-last-child(-n+2) { border-bottom: none; }
          .stockpos-pricing-grid { grid-template-columns: repeat(2, 1fr); }
        }

        /* ---- Mobile / tablet: collapse nav ---- */
        @media (max-width: 860px) {
          .stockpos-nav-links { display: none !important; }
          .stockpos-nav-toggle { display: flex !important; }
          .stockpos-nav-mobile { display: flex; }
        }

        /* ---- Mobile ---- */
        @media (max-width: 640px) {
          .stockpos-nav-inner { padding: 0 1rem; height: 60px; }
          .stockpos-hero-inner { padding: 120px 1.25rem 80px !important; }
          .stockpos-stats-grid { grid-template-columns: repeat(2, 1fr); border-radius: 16px; }
          .stockpos-stat-cell { border-right: 1px solid rgba(255,255,255,0.07); border-bottom: 1px solid rgba(255,255,255,0.07); }
          .stockpos-stat-cell:nth-child(2n) { border-right: none; }
          .stockpos-stat-cell:nth-last-child(-n+2) { border-bottom: none; }
          .stockpos-features-grid { grid-template-columns: 1fr; }
          .stockpos-feature-cell { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
          .stockpos-feature-cell:last-child { border-bottom: none !important; }
          .stockpos-pricing-grid { grid-template-columns: 1fr; }
          .stockpos-step-row { flex-wrap: wrap; gap: 16px !important; padding: 22px 18px !important; }
          .stockpos-step-arrow { display: none !important; }
          .stockpos-footer-inner { flex-direction: column; text-align: center; }
          .stockpos-wave { height: 60px !important; }
        }
      `}</style>

      <Navbar scrolled={scrolled} isLoggedIn={isLoggedIn} navigate={navigate} />
      <Hero />
      <Stats />
      <Features />
      <Wave from="#0A0A1A" to="#11132B" height={120} />
      <HowItWorks />
      <Wave from="#1E1B4B" to="#1E1B4B" height={80} flip />
      <Pricing isLoggedIn={isLoggedIn} navigate={navigate} plans={activePlans} />
      <Wave from="#0A0A1A" to="#060F20" height={80} />
      <Footer />
      <ToastContainer />
    </>
  );
}